import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatUSD, formatDate } from "@/lib/utils";
import {
  Loader2,
  Wallet,
  ShieldCheck,
  ListOrdered,
  CreditCard,
  CheckCircle2,
  Clock,
  XCircle,
  Globe2,
  MessageCircle,
} from "lucide-react";
import { useBranding, whatsappHref } from "@/context/BrandingContext";

const inputCls =
  "flex h-11 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent";

const tabs = [
  { key: "pay", label: "Buy with Card", icon: CreditCard },
  { key: "history", label: "Payment History", icon: ListOrdered },
];

export default function MakePayment() {
  const { t } = useTranslation();
  const { brand } = useBranding();
  const brandName = brand.brand_name || "My GlobalTopup";
  const whatsappUrl = whatsappHref(
    brand.whatsapp_number,
    `Hello ${brandName}, I want to add funds to my wallet but I don't have a card. Please send me another payment method.`,
  );
  const [tab, setTab] = useState("pay");
  const [wallet, setWallet] = useState(null);
  const [topups, setTopups] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const loadWallet = async () => {
    try {
      const { data } = await api.get("/wallet");
      setWallet(data);
    } catch (e) {/* silent */}
  };

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get("/payments/payku/topups?limit=50");
      setTopups(data);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadWallet();
    loadHistory();
  }, []);

  return (
    <div className="space-y-6" data-testid="make-payment-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("nav.make_payment")}</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {t("nav.make_payment")}
        </h1>
        <p className="text-slate-500 text-sm mt-1 max-w-2xl">
          {t("payment.subtitle", "Add funds securely — your wallet balance is always in USD.")}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="border-b border-slate-200 px-3 flex flex-wrap gap-1 overflow-x-auto">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  data-testid={`mp-tab-${t.key}`}
                  onClick={() => {
                    setTab(t.key);
                    if (t.key === "history") loadHistory();
                  }}
                  className={`relative inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold whitespace-nowrap transition-colors ${
                    active ? "text-[#0F62FE]" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                  {active && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[#0F62FE] rounded-full" />}
                </button>
              );
            })}
          </div>
          <div className="p-6">
            {tab === "pay" && <PaykuPayTab />}
            {tab === "history" && <HistoryTab topups={topups} loading={loadingHistory} />}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-[#0F62FE] text-white rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest opacity-85">
              <Wallet className="w-4 h-4" /> Wallet balance
            </div>
            <div className="mt-2 text-3xl font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {formatUSD(wallet?.balance_usd ?? 0)}
            </div>
            <div className="mt-1 text-xs opacity-80">Available for topups</div>
          </div>
          <div className="text-[11px] text-slate-500 flex items-start gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <span>Payments are processed by Payku. We never see or store your card details.</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatCLP(amount) {
  // Show as "CLP $40.000" — unambiguous: "$40.000" alone can be misread as $40.
  try {
    const formatted = new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 }).format(amount);
    return `CLP $${formatted}`;
  } catch {
    return `CLP $${Number(amount).toLocaleString()}`;
  }
}

function PaykuPayTab() {
  const [config, setConfig] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const { brand } = useBranding();
  const brandName = brand.brand_name || "My GlobalTopup";
  const whatsappUrl = whatsappHref(
    brand.whatsapp_number,
    `Hello ${brandName}, I want to add funds to my wallet but I don't have a card. Please send me another payment method.`,
  );

  useEffect(() => {
    api.get("/payments/payku/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const usd = Number(amount) || 0;
  // Payku pricing lives in global app_settings (admin-managed), not on the
  // caller's own user record.
  const rate = Number(config?.payku_usd_clp_rate ?? config?.exchange_rate ?? 0);
  const feePct = Number(config?.payku_extra_fee_pct ?? 0);
  const localAmount = Math.round(usd * rate * (1 + feePct / 100));
  const feeAmount = Math.round(usd * rate * (feePct / 100));
  const isAdmin = config?.role === "MASTER_ADMIN";
  const presets = config?.presets_usd || [10, 25, 50, 100, 250];

  const submit = async (e) => {
    e.preventDefault();
    if (!usd || usd <= 0) {
      toast.error("Enter a valid USD amount");
      return;
    }
    if (!config?.configured) {
      toast.error("Payku is not configured. Please contact your administrator.");
      return;
    }
    if (rate <= 0) {
      toast.error(isAdmin
        ? "The Payku USD → CLP rate is not configured. Set it under Rates & Fees → Payku Payments."
        : "Payku is temporarily unavailable. Please contact your administrator.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/payku/checkout", { amount_usd: usd });
      // Open Payku in the SAME tab — standard redirect-checkout pattern.
      window.location.href = data.redirect_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start the checkout");
      setBusy(false);
    }
  };

  if (!config) {
    return (
      <div className="p-6 text-center text-slate-500 flex items-center justify-center gap-2" data-testid="payku-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  if (!config.configured) {
    return (
      <div className="p-6 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-sm" data-testid="payku-not-configured">
        Payku is not configured on the server. Please contact your administrator.
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="payku-pay-form">
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 flex items-start gap-3">
        <Globe2 className="w-5 h-5 text-[#0F62FE] mt-0.5" />
        <div className="text-sm">
          <div className="font-semibold text-slate-900">Pay in CLP via Payku</div>
          {isAdmin ? (
            <div className="text-slate-600 text-xs mt-0.5" data-testid="payku-admin-fx-note">
              You will be charged in <span className="font-bold">CLP</span> at the platform rate of{" "}
              <span className="font-bold">1 USD = {rate.toLocaleString()} CLP</span>
              {feePct > 0 && <> plus a <span className="font-bold">{feePct}%</span> Payku fee</>}
              . Your wallet is credited in USD on payment success.
            </div>
          ) : (
            <div className="text-slate-600 text-xs mt-0.5" data-testid="payku-user-fx-note">
              You will be charged in <span className="font-bold">CLP</span>. Your wallet is credited in USD on payment success.
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">Amount to add (USD)</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold">$</span>
          <input
            data-testid="payku-amount"
            type="number"
            step="0.01"
            min="1"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={inputCls + " pl-7 text-xl font-bold"}
            required
            autoFocus
          />
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {presets.map((v) => (
            <button
              type="button"
              key={v}
              data-testid={`payku-quick-${v}`}
              onClick={() => setAmount(String(v))}
              className={`h-9 px-3 rounded-lg border text-sm font-semibold ${
                String(v) === String(amount)
                  ? "border-[#0F62FE] bg-[#EDF5FF] text-[#0F62FE]"
                  : "border-slate-300 text-slate-700 hover:bg-slate-50"
              }`}
            >${v}</button>
          ))}
        </div>
        {usd > 0 && (
          <div className="mt-3 rounded-lg bg-[#0F62FE]/5 border border-[#0F62FE]/20 p-3 text-sm" data-testid="payku-fx-preview">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">You pay</span>
              <span className="font-bold text-slate-900" data-testid="payku-total-clp">{formatCLP(localAmount)}</span>
            </div>
            {isAdmin && feePct > 0 && (
              <div className="flex items-center justify-between text-[11px] mt-1 text-slate-500" data-testid="payku-fee-breakdown">
                <span>Includes {feePct}% Payku fee</span>
                <span>{formatCLP(feeAmount)}</span>
              </div>
            )}
            <div className="flex items-center justify-between text-xs mt-1">
              <span className="text-slate-500">Wallet credit</span>
              <span className="font-semibold text-emerald-700">{formatUSD(usd)}</span>
            </div>
          </div>
        )}
      </div>

      <button
        data-testid="payku-submit"
        type="submit"
        disabled={busy || !usd}
        className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-lg bg-[#0F62FE] text-white text-base font-bold hover:bg-[#0050E6] disabled:opacity-50"
      >
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        <CreditCard className="w-4 h-4" />
        Buy with Card{usd ? ` · ${formatUSD(usd)}` : ""}
      </button>

      <div className="text-[11px] text-slate-400">
        You will be redirected to Payku&apos;s secure payment page. Your wallet will be credited automatically after a successful payment.
      </div>

      <WhatsAppFallback whatsappUrl={whatsappUrl} />
    </form>
  );
}

function WhatsAppFallback({ whatsappUrl }) {
  return (
    <div
      data-testid="whatsapp-fallback"
      className="mt-6 rounded-xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 flex items-start gap-4"
    >
      <div className="w-11 h-11 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
        <MessageCircle className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1">
        <div className="font-bold text-slate-900">Don&apos;t have a card?</div>
        <div className="text-sm text-slate-600 mt-0.5">
          Contact us on WhatsApp for other payment methods (bank transfer, cash, crypto, etc.).
        </div>
        <a
          data-testid="whatsapp-contact-btn"
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-2 h-11 px-5 rounded-lg bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 shadow-sm"
        >
          <MessageCircle className="w-4 h-4" />
          Contact us on WhatsApp
        </a>
      </div>
    </div>
  );
}

function HistoryTab({ topups, loading }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="mp-history-tab">
      <table className="w-full text-sm">
        <thead>
          <tr>
            {["Date", "USD credited", "Paid (CLP)", "Status", "Reference"].map((h) => (
              <th key={h} className="bg-slate-50 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 border-b border-slate-200">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
              </td>
            </tr>
          )}
          {!loading && topups.length === 0 && (
            <tr>
              <td colSpan={5} className="px-4 py-10 text-center text-slate-500">
                <div className="text-sm">No funding history yet.</div>
                <div className="text-xs text-slate-400 mt-1">Your Payku top-ups will appear here.</div>
              </td>
            </tr>
          )}
          {!loading && topups.map((t) => <HistoryRow key={t.order_id} topup={t} />)}
        </tbody>
      </table>
    </div>
  );
}

function HistoryRow({ topup }) {
  const statusRaw = topup.credited ? "CREDITED" : (topup.status || "PENDING").toUpperCase();
  const statusMeta = {
    CREDITED: { Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700" },
    SUCCESS: { Icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700" },
    FAILED: { Icon: XCircle, cls: "bg-rose-50 text-rose-700" },
  }[statusRaw] || { Icon: Clock, cls: "bg-amber-50 text-amber-700" };
  const Icon = statusMeta.Icon;
  return (
    <tr className="border-b border-slate-100" data-testid={`history-row-${topup.order_id}`}>
      <td className="px-4 py-3 text-slate-700 text-xs whitespace-nowrap">{formatDate(topup.created_at)}</td>
      <td className="px-4 py-3 font-semibold text-slate-900">{formatUSD(topup.amount_usd)}</td>
      <td className="px-4 py-3 text-slate-700">{formatCLP(topup.local_amount)}</td>
      <td className="px-4 py-3">
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-widest px-2 py-1 rounded-full ${statusMeta.cls}`}>
          <Icon className="w-3 h-3" /> {statusRaw}
        </span>
      </td>
      <td className="px-4 py-3 font-mono text-[11px] text-slate-500 truncate max-w-[180px]" title={topup.payku_token || topup.order_id}>
        {topup.payku_token || topup.order_id}
      </td>
    </tr>
  );
}
